import { useEffect, useMemo, useState } from "react";
import {
  Gender,
  RequestSource,
  type AnalyticsAggregateRow,
  type AnalyticsFieldKey,
} from "@adlywise/shared";
import {
  COUNTRY_OPTIONS,
  DEVICE_OPTIONS,
  KIND_OPTIONS,
} from "@/features/analytics/components/filters/constants";
import { resolveDictIds } from "@/features/analytics/dictApi";
import { searchLookup, type LookupKind } from "@/features/analytics/lookupApi";
import { useAnalyticsApp } from "@/features/analytics/context";

// Client-side id -> name resolution for the aggregated-results group columns
// (plan step 12b). Group columns come back as IDS; the table renders names. We
// split resolution by field kind:
//   - static  (country/deviceType/kind/gender/source/isPaid/livestream): from
//              the same constants/enums the picker uses — no fetch.
//   - context (channel/categories): from the analytics app context.
//   - dict    (region/city/platform): one batched POST /dictionaries/resolve.
//   - lookup  (campaign/segment/creative/provider): the small list endpoints
//              (searchLookup with an empty query returns the whole list).
// Each returns a Record<string, string> keyed by the STRING form of the id.

export type NameMaps = Partial<Record<AnalyticsFieldKey, Record<string, string>>>;

// Resolution result: id→name maps per field key, plus campaign id→icon (a lucide
// IconName) so the table can render the campaign glyph next to its linked name.
export type NameResolution = {
  names: NameMaps;
  campaignIcons: Record<string, string | null>;
};

const asString = (v: unknown): string => String(v);

const optionMap = (
  options: { value: string | number; label: string }[],
): Record<string, string> =>
  Object.fromEntries(options.map((o) => [String(o.value), o.label]));

// Static maps that never change (built once).
const COUNTRY_MAP = optionMap(COUNTRY_OPTIONS);
const DEVICE_MAP = optionMap(DEVICE_OPTIONS);
const KIND_MAP = optionMap(KIND_OPTIONS);
const GENDER_MAP: Record<string, string> = Object.fromEntries(
  Object.values(Gender)
    .filter((v): v is number => typeof v === "number")
    .map((code) => [String(code), String(Gender[code])]),
);
const SOURCE_MAP: Record<string, string> = {
  [String(RequestSource.Vast)]: "VAST (OTT)",
  [String(RequestSource.Rtb)]: "RTB",
};
const BOOL_MAP: Record<string, string> = {
  true: "Yes",
  false: "No",
};

const DICT_KEYS: Record<string, "platforms" | "cities" | "regions"> = {
  region: "regions",
  city: "cities",
  platform: "platforms",
};

const LOOKUP_KEYS: Record<string, LookupKind> = {
  campaign: "campaigns",
  segment: "segments",
  creative: "creatives",
  provider: "providers",
};

// Collect the distinct string ids present for a key across the rows. `categories`
// is a text[] value; its members are collected individually.
const collectIds = (
  rows: AnalyticsAggregateRow[],
  key: AnalyticsFieldKey,
): string[] => {
  const set = new Set<string>();
  for (const row of rows) {
    const v = row[key];
    if (v == null) continue;
    if (Array.isArray(v)) {
      for (const m of v) if (m != null) set.add(asString(m));
    } else {
      set.add(asString(v));
    }
  }
  return [...set];
};

export const useAnalyticsNameMaps = (
  rows: AnalyticsAggregateRow[],
  groupBy: AnalyticsFieldKey[],
): NameResolution => {
  const { availableChannels, availableCategories } = useAnalyticsApp();

  // Static + context maps are derived synchronously from constants/context.
  const staticMaps = useMemo<NameMaps>(() => {
    const channelMap: Record<string, string> = Object.fromEntries(
      availableChannels.map((c) => [String(c.id), c.name]),
    );
    const categoryMap: Record<string, string> = Object.fromEntries(
      availableCategories.map((c) => [String(c.id), c.name]),
    );
    return {
      country: COUNTRY_MAP,
      deviceType: DEVICE_MAP,
      kind: KIND_MAP,
      gender: GENDER_MAP,
      source: SOURCE_MAP,
      isPaid: BOOL_MAP,
      livestream: BOOL_MAP,
      channel: channelMap,
      categories: categoryMap,
    };
  }, [availableChannels, availableCategories]);

  const [asyncMaps, setAsyncMaps] = useState<NameMaps>({});
  const [campaignIcons, setCampaignIcons] = useState<
    Record<string, string | null>
  >({});

  // A stable signature of the ids that need async resolution, so the effect only
  // refetches when the actual set of dict/lookup ids in the rows changes.
  const asyncSignature = useMemo(() => {
    const parts: string[] = [];
    for (const key of groupBy) {
      if (key in DICT_KEYS || key in LOOKUP_KEYS) {
        parts.push(`${key}:${collectIds(rows, key).sort().join(",")}`);
      }
    }
    return parts.join("|");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, groupBy.join(",")]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async (): Promise<void> => {
      // Batch all dict ids into a single /dictionaries/resolve call.
      const dictBody: {
        platforms?: number[];
        cities?: number[];
        regions?: number[];
      } = {};
      for (const key of groupBy) {
        const dict = DICT_KEYS[key];
        if (!dict) continue;
        const ids = collectIds(rows, key)
          .map((s) => Number(s))
          .filter((n) => Number.isInteger(n) && n > 0);
        if (ids.length > 0) dictBody[dict] = ids;
      }

      const lookupPairs: { key: AnalyticsFieldKey; lookup: LookupKind }[] = [];
      for (const key of groupBy) {
        const lookup = LOOKUP_KEYS[key];
        if (lookup) lookupPairs.push({ key, lookup });
      }

      try {
        const [dictResult, ...lookupResults] = await Promise.all([
          Object.keys(dictBody).length > 0
            ? resolveDictIds(dictBody, controller.signal)
            : Promise.resolve(null),
          ...lookupPairs.map((p) =>
            searchLookup(p.lookup, "", controller.signal),
          ),
        ]);

        if (cancelled) return;

        const next: NameMaps = {};

        if (dictResult) {
          const dictToKey: Record<string, AnalyticsFieldKey> = {
            regions: "region",
            cities: "city",
            platforms: "platform",
          };
          for (const [dictName, key] of Object.entries(dictToKey)) {
            const entries =
              dictResult[dictName as keyof typeof dictResult] ?? [];
            if (entries.length === 0) continue;
            next[key] = Object.fromEntries(
              entries.map((e) => [String(e.id), e.name]),
            );
          }
        }

        let nextCampaignIcons: Record<string, string | null> = {};
        lookupPairs.forEach((pair, i) => {
          const entries = lookupResults[i] ?? [];
          next[pair.key] = Object.fromEntries(
            entries.map((e) => [e.id, e.name]),
          );
          // Campaign entries carry an icon (lucide IconName) for the table glyph.
          if (pair.key === "campaign") {
            nextCampaignIcons = Object.fromEntries(
              entries.map((e) => [e.id, e.icon ?? null]),
            );
          }
        });

        setAsyncMaps(next);
        setCampaignIcons(nextCampaignIcons);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        // Name resolution is best-effort — the table falls back to raw ids.
        console.error("analytics name resolution failed", error);
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncSignature]);

  return useMemo(
    () => ({ names: { ...staticMaps, ...asyncMaps }, campaignIcons }),
    [staticMaps, asyncMaps, campaignIcons],
  );
};
